import { useContext, useEffect, useState, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  FaHome,
  FaClock,
  FaSun,
  FaMoon,
  FaChevronDown,
  FaBullseye,
  FaUsers,
  FaChartBar,
  FaBell,
  FaStore,
} from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { RxHamburgerMenu } from "react-icons/rx";
import { GrCatalog } from "react-icons/gr";
import { AiOutlineLogout } from "react-icons/ai";
import {
  // BsArrowsCollapseVertical,
  // BsArrowsExpandVertical,
  BsFillCreditCard2FrontFill,
} from "react-icons/bs";
import { MdAccountBalanceWallet, MdEditNote } from "react-icons/md";

import logo from "../assets/logo.png";
import darkModeLogo from "../assets/darkMode_logo.png";
import error_logo from "../assets/error_logo.png";

import { useAuth } from "../Context/AuthContext";
import { DarkModeContext } from "../Context/DarkModeContext.jsx";
import { GeoContext } from "../Context/GeoContext.js";
import EditProfileModal from "./ProfileSettingsModal.jsx";
import StudentGlobalActions from "./StudentGlobalActions.jsx";
import AccountBalance from "./AccountBalance.jsx";
import useDesktopViewport from "../hooks/useDesktopViewport";

function Layout() {
  const isDesktop = useDesktopViewport();
  const { logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useContext(DarkModeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { store } = useContext(GeoContext);

  const [userName, setUserName] = useState(() =>
    localStorage.getItem("userName"),
  );
  const [activeItem, setActiveItem] = useState();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Sidebar remains expanded; mobile navigation uses the drawer.
  const [isCollapsed] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const accountRef = useRef(null);
  const accountButtonRef = useRef(null);
  useEffect(() => {
    if (!isDesktop && (showProfileMenu || showProfileModal)) {
      setIsSidebarOpen(false);
    }
  }, [isDesktop, showProfileMenu, showProfileModal]);

  useEffect(() => {
    setShowProfileMenu(false);
    setIsSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!showProfileMenu) return;
    const outside = (event) => {
      if (!accountRef.current?.contains(event.target))
        setShowProfileMenu(false);
    };
    const escape = (event) => {
      if (event.key === "Escape") {
        setShowProfileMenu(false);
        accountButtonRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [showProfileMenu]);

  useEffect(() => {
    const img = new Image();
    img.src = error_logo;
  }, []);

  useEffect(() => {
    if (location.pathname === "/home") {
      setActiveItem("Home");
    } else if (location.pathname === "/live-sessions") {
      setActiveItem("Live Sessions");
    } else if (location.pathname === "/my-courses") {
      setActiveItem("My Courses");
    } else if (location.pathname === "/wallet") {
      setActiveItem("Wallet");
    } else if (location.pathname === "/catalogue") {
      setActiveItem("Catalogue");
    } else {
      setActiveItem(null);
    }
  }, [location]);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => {
      const next = !prev;
      if (!next) {
        // when closing the sidebar on mobile, revert to main menu
        setShowProfileMenu(false);
      }
      return next;
    });
  };

  const walletUrl = "https://payment.edu-high.com/";

  const handleNavigate = (path) => navigate(path, { replace: true });
  const handleLogout = async (e) => {
    e.preventDefault();
    await logout();
  };

  // --- Main Sidebar Navigation ---
  const mainNav = (
    <motion.ul
      key="main-menu"
      initial={{ opacity: 0, x: -30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 30 }}
      transition={{ duration: 0.3 }}
      className="w-full flex-grow space-y-0.5 px-3 text-base md:space-y-1.5"
    >
      <li
        onClick={() => handleNavigate("/home")}
        className={`flex cursor-pointer items-center rounded-xl px-3 py-2.5 md:py-3.5 font-medium transition-all hover:bg-blue-50 hover:text-[#06315B] ${
          activeItem === "Home"
            ? isDarkMode
              ? "bg-[var(--study-raised)] text-[var(--study-text)] ring-1 ring-inset ring-[var(--study-border)]"
              : "bg-[#06315B] text-white"
            : ""
        } ${isCollapsed && "justify-center"}`}
      >
        <FaHome className="text-xl" />
        {!isCollapsed && <span className="ml-4">Home</span>}
      </li>

      <li
        onClick={() => handleNavigate("/catalogue")}
        className={`flex cursor-pointer items-center rounded-xl px-3 py-2.5 md:py-3.5 font-medium transition-all hover:bg-blue-50 hover:text-[#06315B] ${
          activeItem === "Catalogue"
            ? isDarkMode
              ? "bg-[var(--study-raised)] text-[var(--study-text)] ring-1 ring-inset ring-[var(--study-border)]"
              : "bg-customBlue text-white"
            : ""
        } ${isCollapsed && "justify-center"}`}
      >
        <GrCatalog className="text-xl" />
        {!isCollapsed && <span className="ml-4">Catalogue</span>}
      </li>

      {[
        {
          label: "Store",
          href: "https://store.edu-high.com/",
          Icon: FaStore,
        },
        {
          label: "Practice",
          href: "https://app.edu-high.com/practice",
          Icon: FaBullseye,
        },
        {
          label: "Community",
          href: "https://app.edu-high.com/community",
          Icon: FaUsers,
        },
        {
          label: "My Performance",
          href: "https://app.edu-high.com/performance",
          Icon: FaChartBar,
        },
      ].map(({ label, href, Icon }) => (
        <li key={label}>
          <a
            href={href}
            // target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            title={isCollapsed ? label : undefined}
            className={`flex items-center rounded-xl px-3 py-2.5 md:py-3.5 font-medium transition-colors hover:bg-blue-50 hover:text-[#06315B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${isCollapsed ? "justify-center" : "gap-4"}`}
          >
            <Icon className="shrink-0 text-xl" />
            {!isCollapsed && (
              <>
                <span className="flex-1">{label}</span>
              </>
            )}
          </a>
        </li>
      ))}

      <hr className="border border-gray-800 w-full max-w-[200px] mx-auto" />
      <li
        onClick={() => handleNavigate("/live-sessions")}
        className={`flex cursor-pointer items-center rounded-xl px-3 py-2.5 md:py-3.5 font-medium transition-all hover:bg-blue-50 hover:text-[#06315B] ${
          activeItem === "Live Sessions"
            ? isDarkMode
              ? "bg-[var(--study-raised)] text-[var(--study-text)] ring-1 ring-inset ring-[var(--study-border)]"
              : "bg-customBlue text-white"
            : ""
        } ${isCollapsed && "justify-center"}`}
      >
        <FaClock className="text-xl" />
        {!isCollapsed && <span className="ml-4">Live Sessions</span>}
      </li>

      <li
        onClick={() => handleNavigate("/wallet")}
        className={`flex cursor-pointer items-center rounded-xl px-3 py-2.5 md:py-3.5 font-medium transition-all hover:bg-blue-50 hover:text-[#06315B] ${
          activeItem === "Wallet"
            ? isDarkMode
              ? "bg-[var(--study-raised)] text-[var(--study-text)] ring-1 ring-inset ring-[var(--study-border)]"
              : "bg-customBlue text-white"
            : ""
        } ${isCollapsed && "justify-center"}`}
      >
        <BsFillCreditCard2FrontFill className="text-xl" />
        {!isCollapsed && <span className="ml-4">Wallet</span>}
      </li>
    </motion.ul>
  );

  return (
    <div
      className={`flex min-h-screen ${
        isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-black"
      }`}
    >
      <StudentGlobalActions />
      {/* Hamburger Menu */}
      {!isSidebarOpen && !showProfileModal && (
        <nav
          onClick={() => {
            setShowProfileMenu(false);
            setIsSidebarOpen(true);
          }}
          className={`fixed left-4 top-4 z-50 cursor-pointer rounded-xl bg-customRed p-3 text-white shadow-lg transition hover:scale-105 lg:hidden`}
        >
          <div className="items-center">
            <RxHamburgerMenu size={25} />
          </div>
        </nav>
      )}

      {isSidebarOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={toggleSidebar}
          className="fixed inset-0 z-30 bg-slate-950/45 backdrop-blur-[2px] lg:hidden"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 z-40 h-dvh border-r shadow-xl transition-all duration-300 ease-in-out
        ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0
        ${isCollapsed ? "w-[76px]" : "w-[248px]"} 
        ${isDarkMode ? "border-gray-700 bg-[#171b22] text-white" : "border-slate-200 bg-white text-customBlue"}`}
      >
        <div className="flex flex-col items-center h-full">
          {/* Logo */}
          <div
            className={`flex min-h-[96px] w-full items-center justify-center max-lg:pl-4 max-lg:pr-16 border-b ${
              isDarkMode ? "border-gray-700" : "border-slate-100"
            } ${isCollapsed ? "py-5" : "px-6 py-4"}`}
          >
            <img
              src={error_logo}
              alt="Collapsed Logo"
              className={`w-10 h-10 object-contain absolute transition-all duration-300 ${
                isCollapsed ? "opacity-100 scale-100" : "opacity-0 scale-75"
              }`}
            />
            <img
              src={isDarkMode ? darkModeLogo : logo}
              alt="Logo"
              className={`w-42 h-21 object-contain transition-all duration-300 ${
                isCollapsed ? "opacity-0 scale-75" : "opacity-100 scale-100"
              }`}
            />
          </div>

          {/* Close Sidebar (mobile) */}
          <button
            type="button"
            aria-label="Close sidebar"
            title="Close sidebar"
            className={`group absolute right-3 top-7 lg:hidden flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-lg transition duration-200 hover:border-customRed hover:bg-customRed hover:text-white active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customRed focus-visible:ring-offset-2 dark:border-[var(--study-border)] dark:bg-[var(--study-raised)] dark:text-[var(--study-text)] dark:hover:bg-customRed motion-reduce:transition-none`}
            onClick={() => setIsSidebarOpen(false)}
          >
            <IoClose className="text-xl transition-transform duration-200 group-hover:rotate-90 motion-reduce:transition-none" />
          </button>

          {/* Sidebar Content Flip Animation */}
          <div
            onClick={(event) => {
              if (window.innerWidth < 1024 && event.target.closest("li")) {
                setIsSidebarOpen(false);
              }
            }}
            className="relative min-h-0 w-full flex-1 overflow-y-auto py-3"
          >
            <AnimatePresence mode="wait">{mainNav}</AnimatePresence>
          </div>

          {/* Country / Store */}
          {store && (
            <div
              className={`mx-3 mb-3 flex items-center gap-2 rounded-xl border p-3 ${isDarkMode ? "border-gray-700 bg-white/5" : "border-slate-200 bg-slate-50"}`}
            >
              <img
                src={`https://flagcdn.com/w20/${store.countryCode.toLowerCase()}.png`}
                alt={store.country}
                className="w-6 h-4 rounded-sm shadow"
              />
              {!isCollapsed && (
                <span className="text-base font-medium">
                  {store.name} - {store.currency}
                </span>
              )}
            </div>
          )}

          {/* Controls */}
          <div className="flex w-full gap-2 px-3 pb-3">
            <button
              onClick={() => {
                if (isDarkMode) toggleDarkMode();
              }}
              aria-label="Light theme"
              title="Light theme"
              className={`flex w-full items-center justify-center gap-2 rounded-xl border-2 px-3 py-2.5 transition-all duration-300 ${
                !isDarkMode
                  ? "border-yellow-500 bg-yellow-400 text-white opacity-100 shadow-md"
                  : "border-yellow-500/40 text-yellow-400 opacity-40 hover:opacity-70"
              }`}
            >
              <FaSun />
              <span className="text-xs font-semibold">Light</span>
            </button>

            <button
              onClick={() => {
                if (!isDarkMode) toggleDarkMode();
              }}
              aria-label="Navy theme"
              title="Navy theme"
              className={`flex w-full items-center justify-center gap-2 rounded-xl border-2 px-3 py-2.5 transition-all duration-300 ${
                isDarkMode
                  ? "border-[var(--study-border)] bg-[var(--study-raised)] text-[var(--study-text)] opacity-100 shadow-md"
                  : "border-[#06315B]/40 text-[#06315B] opacity-40 hover:opacity-70"
              }`}
            >
              <FaMoon />
              <span className="text-xs font-semibold">Navy</span>
            </button>
            {/* <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className={`flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 transition-colors duration-300 ${
                isDarkMode
                ? "border border-[var(--study-border)] text-[var(--study-muted)] hover:bg-white/5 hover:text-white"
                : "border-2 border-customBlue text-customBlue hover:bg-customBlue hover:text-white"
              }`}
              >
              {isCollapsed ? (
                <BsArrowsExpandVertical className="text-xl" />
              ) : (
                <BsArrowsCollapseVertical className="text-xl" />
              )}
            </button> */}
          </div>
        </div>
      </aside>
      <main
        className={`min-w-0 flex-1 [&>*]:min-w-0 [&>*]:max-w-full transition-all duration-300 ${isCollapsed ? "lg:ml-[76px]" : "lg:ml-[248px]"}`}
      >
        <header
          aria-label="Account and notifications"
          className="fixed right-2 top-3 z-30 max-w-[calc(100vw-5rem)] flex items-center gap-1 rounded-2xl border border-white/15 bg-[#082747]/95 p-1.5 text-white shadow-xl backdrop-blur-xl sm:right-6 sm:gap-3"
        >
          <a
            href={walletUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Fill Wallet"
            title="Fill Wallet"
            className="group/wallet flex h-10 shrink-0 items-center rounded-xl bg-emerald-400/10 text-emerald-300 transition-colors hover:bg-emerald-400/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 sm:h-11"
          >
            <span className="flex w-8 shrink-0 items-center justify-center sm:w-11">
              <MdAccountBalanceWallet size={21} aria-hidden="true" />
            </span>
            <span className="grid grid-cols-[0fr] transition-[grid-template-columns,opacity] duration-300 ease-out opacity-0 group-hover/wallet:grid-cols-[1fr] group-hover/wallet:opacity-100 group-focus-visible/wallet:grid-cols-[1fr] group-focus-visible/wallet:opacity-100 motion-reduce:transition-none">
              <span className="overflow-hidden whitespace-nowrap text-sm font-semibold">
                <span className="pr-3">Fill Wallet</span>
              </span>
            </span>
          </a>
          <a
            href="https://app.edu-high.com/notifications"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Notifications"
            title="Notifications"
            className="group/bell flex h-10 w-8 shrink-0 sm:h-11 sm:w-11 items-center justify-center rounded-xl border-r border-white/10 text-xl transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <FaBell className="origin-top motion-safe:group-hover/bell:animate-bellShake motion-safe:group-focus-visible/bell:animate-bellShake" />
          </a>
          <div ref={accountRef} className="relative min-w-0">
            <button
              ref={accountButtonRef}
              type="button"
              aria-label="Account"
              aria-expanded={showProfileMenu}
              aria-controls="account-dropdown"
              onClick={() => {
                if (!isDesktop) setIsSidebarOpen(false);
                setShowProfileMenu((open) => !open);
              }}
              className="flex w-full min-w-0 items-center gap-2 sm:gap-3 rounded-xl p-2 text-left hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white"
            >
              <span
                className="flex h-9 w-9 shrink-0 sm:h-11 sm:w-11 items-center justify-center rounded-full bg-white font-bold text-blue-700"
                aria-hidden="true"
              >
                {(userName || "Student")
                  .trim()
                  .split(/\s+/)
                  .map((part) => part[0])
                  .slice(0, 2)
                  .join("")}
              </span>
              <span className="block min-w-0 max-w-[72px] truncate pb-4 text-sm font-semibold sm:max-w-[140px] sm:text-base">
                {userName || "Student"}
              </span>
              <FaChevronDown />
            </button>
            <div className="absolute bottom-2 left-[52px] right-6 sm:left-16 sm:right-7">
              <AccountBalance />
            </div>

            <AnimatePresence>
              {showProfileMenu && (
                <motion.div
                  id="account-dropdown"
                  initial={{ opacity: 0, y: -8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.97 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  className={`absolute right-0 top-full z-50 mt-3 w-60 max-w-[calc(100vw-2rem)] max-h-[calc(100dvh-6rem)] overflow-y-auto origin-top-right rounded-2xl border p-2 shadow-2xl backdrop-blur-xl ${
                    isDarkMode
                      ? "border-white/10 bg-[#10263c]/95 text-white"
                      : "border-slate-200/80 bg-white/95 text-slate-800"
                  }`}
                >
                  {/* Edit Profile */}
                  <button
                    type="button"
                    onClick={() => {
                      setShowProfileMenu(false);
                      if (!isDesktop) setIsSidebarOpen(false);
                      setShowProfileModal(true);
                    }}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition ${
                      isDarkMode ? "hover:bg-white/10" : "hover:bg-slate-100"
                    }`}
                  >
                    <span
                      className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                        isDarkMode
                          ? "bg-white/10 text-slate-200"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      <MdEditNote className="text-lg" />
                    </span>

                    <span className="flex-1 text-left">Edit Profile</span>
                  </button>

                  {/* Separator */}
                  <div
                    className={`my-1 border-t ${
                      isDarkMode ? "border-white/10" : "border-slate-100"
                    }`}
                  />

                  {/* Logout */}
                  <button
                    type="button"
                    onClick={handleLogout}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-red-500 transition ${
                      isDarkMode ? "hover:bg-red-500/10" : "hover:bg-red-50"
                    }`}
                  >
                    <span
                      className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                        isDarkMode ? "bg-red-500/10" : "bg-red-50"
                      }`}
                    >
                      <AiOutlineLogout className="text-lg" />
                    </span>

                    <span className="flex-1 text-left">Logout</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </header>
        <Outlet context={{ userName }} />
        <EditProfileModal
          showModal={showProfileModal}
          setShowModal={setShowProfileModal}
          onUpdateUsername={setUserName}
        />
      </main>
    </div>
  );
}
export default Layout;
